
import type { PropType } from 'vue'
import { defineComponent, watch, reactive, computed } from 'vue'

// > services/types <
import { Waybill, SendRequestForPaymentPayload } from '@/modern/modules/m-dispatcher-module/services/types/waybill.types'

// > services/waybills
import waybillsAPI from '@/modern/modules/m-dispatcher-module/services/waybill.service'

import RadioOption from './RadioOption.vue'
import formatPriceToTenge from '@/modern/helpers/formatPriceToTenge'

import PHONE_MASK from '@/modern/masks/phone.mask'
import CARD_NUMBER_MASK from '@/modern/masks/card-number.mask'
import PRICE_MASK from '@/modern/masks/price.mask'

// > legacy/validation <
import useVuelidate from '@vuelidate/core'
import { required, requiredIf } from '@vuelidate/validators'

// > legacy/notifications <
import useNotifications from '@/common/composable/useNotifications'

export default defineComponent({
    components: { RadioOption },
    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
        modelValue: {
            type: Boolean,
            default: false,
        },
        waybill: {
            type: Object as PropType<Waybill | null>,
            default: null
        }
    },
    emits: ['update:modelValue', 'close', 'sended'],
    setup(props, { emit }) {
        const notifications = useNotifications()

        const state = reactive({
            isLoading: false,
            paymentSourceId: 10,
            paymentMethodId: 1,
            transferTypeId: 1,
            totalAmount: null as string | null,
            receiverFullName: null as string | null,
            phoneNumber: null as string | null,
            cardNumber: null as string | null,
        })

        const state_form$ = useVuelidate({
            receiverFullName: { required },
            totalAmount: { required: requiredIf(() => state.paymentSourceId === 13) },
            phoneNumber: { required: requiredIf(() => state.transferTypeId === 1) },
            cardNumber: { required: requiredIf(() => state.transferTypeId === 2) },
        }, state)

        const onUpdateModelValue = (newValue: boolean) => {
            if (props.disabled) return
            emit('update:modelValue', newValue)
        }

        const onSelectPaymentSource = (paymentSourceId: number) => {
            state.paymentSourceId = paymentSourceId

            if (paymentSourceId !== 13) {
                state.totalAmount = null
            }
        }

        const onSelectTransferTypeId = (transferTypeId: number) => {
            state.transferTypeId = transferTypeId

            if (transferTypeId === 1) {
                state.cardNumber = null
            } else if (transferTypeId === 2) {
                state.phoneNumber = null
            }
        }

        const onSubmit = async () => {
            if (!props.waybill) return

            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            try {
                state.isLoading = true

                let totalAmount = 0

                if (state.paymentSourceId === 10) {
                    totalAmount = Number(props.waybill.ndsTotalAmount)
                } else if (state.paymentSourceId === 11) {
                    totalAmount = Number(props.waybill.additionalServicesTotalAmount)
                } else if (state.paymentSourceId === 12) {
                    totalAmount = total.value
                } else if (state.paymentSourceId === 13) {
                    totalAmount = Number((state.totalAmount as string).replace(/\D/g, ""))
                } 

                let number = ''

                if (state.transferTypeId === 1) {
                    number = state.phoneNumber as string
                } else if (state.transferTypeId === 2) {
                    number = state.cardNumber as string
                }

                const payload: SendRequestForPaymentPayload = {
                    paymentSourceId: state.paymentSourceId,
                    paymentMethodId: state.paymentMethodId,
                    transferTypeId: state.transferTypeId,
                    totalAmount,
                    receiverFullName: state.receiverFullName as string,
                    number
                }

                await waybillsAPI.sendOnRequestForPayment(props.waybill.id, payload)
                notifications.notify({ type: 'success', title: 'Запрос на выплату отправлен', description: `Запрос на выплату отправлен` })

                onUpdateModelValue(false)
                emit('sended')
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При отправке запроса на выплату произошла ошибка' })
            } finally {
                state.isLoading = false
            }
        }

        watch(
            () => props.modelValue,
            () => {
                state.isLoading = false
                state.paymentSourceId = 10
                state.paymentMethodId = 1
                state.transferTypeId = 1
                state.receiverFullName = null
                state.totalAmount = null
                state.phoneNumber = null
                state.cardNumber = null
                state_form$.value.$reset()
            }
        )

        const total = computed(() => {
            if (props.waybill) {
                const additionalServicesTotalAmount = Number(props.waybill.additionalServicesTotalAmount)
                const ndsTotalAmount = Number(props.waybill.ndsTotalAmount)
                return additionalServicesTotalAmount + ndsTotalAmount
            }

            return 0
        })

        const totalValue = computed(() => {
            if (props.waybill) {
                if (state.paymentSourceId === 10) {
                    return props.waybill.ndsTotalAmount
                }

                if (state.paymentSourceId === 11) {
                    return props.waybill.additionalServicesTotalAmount
                }

                if (state.paymentSourceId === 12) {
                    return total.value
                }

                if (state.paymentSourceId === 13) {
                    return state.totalAmount
                }
            }

            return 0
        })

        return {
            state,
            state_form$,
            onUpdateModelValue,
            onSelectPaymentSource,
            onSelectTransferTypeId,
            formatPriceToTenge,
            total,
            totalValue,
            PRICE_MASK,
            PHONE_MASK,
            CARD_NUMBER_MASK,
            onSubmit,
        }
    }
})
